import Vue from 'vue'
import {
  findBoatTypeById,
  findBoatTypeBySlug,
  findCharterTypeById, findCharterTypeBySlug,
  findFieldByKey,
  getBoatTypeSlugs
} from '~/utils/search/searchHelper'
import {
  SEARCH_QUERY_TYPE,
  INITIAL_SEARCH_TERMS,
  SEARCH_PARAMS_TYPE,
  SEARCH_RESETTABLE_ATTRIBUTES,
  SEARCH_BOAT_ANY_TYPE_ID,
  SEARCH_ONLY_RESETTABLE_ATTRIBUTE,
  getSyncedParams,
  SEARCH_SOMETIMES_RESETTABLE_ATTRIBUTE, SEARCH_BOAT_ANY_TYPE
} from '~/utils/search/search.constants'
import { exists } from '~/utils/utils'
import { getCallerFunctionName, isObject, isRealNaN, isValidDateFormat } from '~/utils/v'
import dayjs from '~/modules/dayjs'
import { SearchFilter } from '~/utils/search/search-filter.class'

export const SEARCH_STRING_ATTRIBUTE = 'string'
export const SEARCH_LIST_ATTRIBUTE = 'list'
export const SEARCH_MINMAX_ATTRIBUTE = 'minmax'
export const SEARCH_MINMAX_RANGES_ATTRIBUTE = 'minmaxRenges'
export const SEARCH_INTEGER_ATTRIBUTE = 'integer'
export const SEARCH_BOOLEAN_ATTRIBUTE = 'boolean'

export class SearchAttribute extends SearchFilter {
  filters = Vue.observable({})
  instance = Vue.observable({})

  constructor (ctx) {
    super()
    if (ctx) {
      this.ctx = ctx
      this.store = ctx.store
      this.route = ctx.route

      this.charterTypes = this.store.getters['data/chartered']
      this.boatTypes = this.store.getters['data/boatTypes']
      this.location = null
    }
  }

  // MethodName Getter Methods

  getAttributeSetterMethodName (key) {
    const attributeType = this.getAttributeType(key)
    if (attributeType) {
      key = attributeType.key
    }

    const methodName = `set${key
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('')}Attribute`

    if (typeof this[methodName] === 'function') {
      return methodName
    } else {
      return 'defaultAttributeSetter'
    }
  }

  getAttributeResetMethodName (key) {
    const attributeType = this.getAttributeType(key)
    if (attributeType) {
      key = attributeType.key
    }

    const methodName = `reset${key
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('')}Attribute`

    if (typeof this[methodName] === 'function') {
      return methodName
    } else {
      return 'defaultAttributeReset'
    }
  }

  // Default Setter Methods

  defaultAttributeSetter (key, value, force = false) {
    const attributeType = this.getAttributeType(key)

    if (!attributeType) {
      return
    }
    const dataKey = attributeType.key

    if ((value === 'null' && attributeType.type === SEARCH_STRING_ATTRIBUTE)) {
      value = null
    }
    if (value === '[object Object]' || isRealNaN(value)) {
      console.error(`Wrong value for attribute with key: ${dataKey}`)
      return
    }

    if (force === true) {
      // console.info(`Force set value ${JSON.stringify(value)} for key ${dataKey}`)
    } else {
      force = false
    }

    switch (attributeType.type) {
      case SEARCH_STRING_ATTRIBUTE:
        if ([null, undefined].includes(value)) {
          Vue.set(this.instance, dataKey, value)
        } else {
          Vue.set(this.instance, dataKey, `${value}`)
        }
        break
      case SEARCH_LIST_ATTRIBUTE:
        if (typeof value === 'string' || typeof value === 'number') {
          const array = [...new Set(value.split(','))]
          Vue.set(this.instance, dataKey, attributeType.listOf === SEARCH_INTEGER_ATTRIBUTE
            ? array.map(item => parseInt(item, 10))
            : array)
          break
        }
        if (Array.isArray(value)) {
          const uniqueArray = [...new Set(value)].filter(i => !isRealNaN(i)).filter(Boolean)
          Vue.set(this.instance, dataKey, attributeType.listOf === SEARCH_INTEGER_ATTRIBUTE
            ? uniqueArray.map(item => parseInt(item, 10))
            : uniqueArray)
          break
        }
        if (force) {
          Vue.set(this.instance, dataKey, value)
          break
        }
        break
      case SEARCH_MINMAX_ATTRIBUTE:
        const range = this.getFilterRange(key)

        // if (Object.values(range).find(i => i === 0 || i === null || i === undefined) !== undefined) {
        //   force = true
        // }
        //
        // if (!(force && [value?.min, value?.max].filter(i => i === null || i === undefined).length === 2)) {
        //   force = false
        // }

        if (typeof value === 'string') {
          const [min, max] = value.split(',')
          Vue.set(this.instance, dataKey, {
            min: min ? parseFloat(min) : range.min,
            max: max ? parseFloat(max) : range.max
          })
          break
        }
        if (isObject(value)) {
          Vue.set(this.instance, dataKey, {
            min: value.min || range.min,
            max: value.max || range.max
          })
          break
        }
        Vue.set(this.instance, dataKey, range)
        break
      case SEARCH_INTEGER_ATTRIBUTE:
        if (typeof value === 'string' || typeof value === 'number') {
          const intValue = parseInt(value, 10)
          if (!isNaN(intValue)) {
            Vue.set(this.instance, dataKey, intValue)
          }
        }
        if (force || value === null) {
          Vue.set(this.instance, dataKey, value)
          break
        }
        break
      case SEARCH_BOOLEAN_ATTRIBUTE:
        if (typeof value === 'boolean') {
          Vue.set(this.instance, dataKey, value)
        } else if (typeof value === 'string') {
          Vue.set(this.instance, dataKey, value === 'true')
        } else if (typeof value === 'number') {
          Vue.set(this.instance, dataKey, value === 1)
        } else {
          Vue.set(this.instance, dataKey, null)
        }
        break
      default:
        // console.warn(`No handler found for attribute with key ${dataKey} and type ${attributeType.type}`)
        break
    }
  }

  // Default Reset Methods

  defaultAttributeReset (key, force = false) {
    const attributeType = this.getAttributeType(key)
    if (force !== true) {
      force = false
    }
    if (!attributeType) {
      return
    }
    const dataKey = attributeType.key

    if (!SEARCH_RESETTABLE_ATTRIBUTES.includes(dataKey) && !SEARCH_SOMETIMES_RESETTABLE_ATTRIBUTE.includes(dataKey)) {
      // console.error(`AttributeType ${dataKey} is not resettable`)
      if (!force) {
        return
      } else {
        console.warn(`But forcing reset for non-resettable attribute ${dataKey}`)
      }
    }

    getSyncedParams(dataKey).forEach((key) => {
      if (attributeType.type === SEARCH_MINMAX_ATTRIBUTE) {
        this.setAttributeValue(key, { min: null, max: null }, true)
      } else {
        this.setAttributeValue(key, this.getDefaultAttributeValue(key), force)
      }
    })
  }

  // Main Attribute Methods

  setAttributeValue (key, value, force = false) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      return
    }
    if (force !== true) {
      force = false
    }
    const dataKey = attributeType.dataKey || attributeType.key

    this[this.getAttributeSetterMethodName(dataKey)](dataKey, value, force)
  }

  getAttributeValue (key) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      return
    }
    const dataKey = attributeType.dataKey || attributeType.key
    const value = this.getSearchTerms()[dataKey]
    let result = null
    switch (attributeType.type) {
      case SEARCH_STRING_ATTRIBUTE:
        result = `${value}`
        break
      case SEARCH_INTEGER_ATTRIBUTE:
        result = parseInt(value, 10)
        break
      case SEARCH_BOOLEAN_ATTRIBUTE:
        result = value !== null ? !!value : null
        break
      default:
        result = value
        break
    }
    return result
  }

  isFilterValueSelected (key, value) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      return false
    }

    const dataKey = attributeType.dataKey || attributeType.key
    const currentValue = this.getAttributeValue(dataKey)

    if (!this.isChangedAttribute(dataKey)) {
      return false
    }

    switch (attributeType.type) {
      case SEARCH_STRING_ATTRIBUTE:
        return currentValue === value

      case SEARCH_INTEGER_ATTRIBUTE:
        return parseInt(currentValue, 10) === parseInt(value, 10)

      case SEARCH_BOOLEAN_ATTRIBUTE:
        return !!currentValue === !!value

      case SEARCH_LIST_ATTRIBUTE:
        if (Array.isArray(currentValue)) {
          const compareValue = attributeType.listOf === SEARCH_INTEGER_ATTRIBUTE
            ? parseInt(value, 10)
            : value
          return currentValue.includes(compareValue)
        }
        return false

      case SEARCH_MINMAX_ATTRIBUTE:
        if (typeof value === 'object' && value !== null) {
          return currentValue.min === value.min && currentValue.max === value.max
        }
        return false

      default:
        return false
    }
  }

  getDefaultAttributeValue (key) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      return
    }
    key = attributeType.key
    return INITIAL_SEARCH_TERMS()[key]
  }

  // Main Reset Methods

  resetAllAttributes (force = false) {
    if (force !== true) {
      force = false
    }
    Object.keys(this.getSearchTerms()).forEach((key) => {
      this.resetAttributeValue(key, force)
    })
  }

  softResetAllAttributes () {
    Object.keys(this.getSearchTerms()).forEach((key) => {
      if (['start_date', 'duration'].includes(key)) {
        return
      }
      if (['boat_types', 'boatType'].includes(key) && this.getAttributeValue('boat_types')?.length === 1) {
        return
      }
      this.resetAttributeValue(key, true)
    })
  }

  resetAttributeValue (key, force = true) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      return
    }
    const dataKey = attributeType.dataKey || attributeType.key
    if (!SEARCH_RESETTABLE_ATTRIBUTES.includes(dataKey) && !SEARCH_SOMETIMES_RESETTABLE_ATTRIBUTE.includes(dataKey)) {
      // console.warn('Do not attempt to reset the router parameter directly', key)
      return
    } else if (force === true) {
      // console.info('Force reset attribute with key ' + key)
    }
    //console.log('key: ' + key + '; method: ' + this.getAttributeResetMethodName(key))
    return this[this.getAttributeResetMethodName(key)](key, force)
  }

  // Other Methods

  isResettable () {
    return !!Object.keys(this.getClearSearchTerms()).find(i => SEARCH_RESETTABLE_ATTRIBUTES.includes(i))?.length
  }

  checkAttributeChangeStatus (key, value) {
    const filterType = this.getAttributeType(key)
    const defaultValue = this.getDefaultAttributeValue(key)

    if (filterType.type === SEARCH_MINMAX_ATTRIBUTE) {
      const filter = this.filters[filterType.responseKey || key]
      const filterRange = {
        min: Array.isArray(filter) ? filter[0]?.key : null,
        max: Array.isArray(filter) ? filter[filter.length - 1]?.key : null
      }
      const parsedValue = {
        min: parseFloat(value?.min),
        max: parseFloat(value?.max)
      }

      if (!Object.values(filterRange || {}).length || Object.values(filterRange).filter(i => i === 0 || i === undefined || i === null).length === 2) {
        if (Object.values(parsedValue).filter(i => i === 0).length < 2) {
          return true
        } else {
          return false
        }
      }

      if (filterRange.min !== null && filterRange.max !== null) {
        const parsedFilterRange = {
          min: parseFloat(filterRange.min),
          max: parseFloat(filterRange.max)
        }
        if (!isNaN(parsedValue.min) && !isNaN(parsedValue.max)) {
          const minMatch = parsedValue.min === parsedFilterRange.min
          const maxMatch = parsedValue.max === parsedFilterRange.max
          if (minMatch && maxMatch) {
            return false
          }
          if ((parsedFilterRange.max !== parsedValue.min) || (parsedFilterRange.max !== parsedValue.max)) {
            return true
          }
        } else {
          return false
        }
      } else if (isNaN(parsedValue.min) || isNaN(parsedValue.max)) {
        return false
      } else if ((parsedValue.min !== 0 && parsedValue.max !== 0)) {
        return true
      } else {
        // PLEASE DON`T CHANGE IT :( idk
        return true
      }
    }
    if (filterType.type === SEARCH_STRING_ATTRIBUTE) {
      if (!exists(value)) {
        return false
      }
      if (!defaultValue && !value) {
        return false
      }
      if (!defaultValue && value === 'null') {
        return false
      }
      return value !== defaultValue
    }
    if (filterType.type === SEARCH_BOOLEAN_ATTRIBUTE) {
      if (filterType.type === SEARCH_BOOLEAN_ATTRIBUTE) {
        const normalizedValue = value === 'true' || value === true || value === 1
          ? true
          : value === 'false' || value === false || value === 0
            ? false
            : null

        const normalizedDefault = defaultValue === 'true' || defaultValue === true || defaultValue === 1
          ? true
          : defaultValue === 'false' || defaultValue === false || defaultValue === 0
            ? false
            : null

        return normalizedValue !== normalizedDefault
      }
    }
    if (filterType.type === SEARCH_LIST_ATTRIBUTE) {
      if (Array.isArray(value) && Array.isArray(defaultValue)) {
        return JSON.stringify(value) !== JSON.stringify(defaultValue)
      }
      if (!defaultValue && !value) {
        return false
      }
    }
    if (filterType.type === SEARCH_INTEGER_ATTRIBUTE) {
      if (value && parseInt(value, 10) !== parseInt(defaultValue, 10)) {
        return true
      }
    }

    return false
  }

  getClearSearchTerms () {
    const result = {}
    for (const [key, value] of Object.entries(this.getSearchTerms())) {
      if (this.checkAttributeChangeStatus(key, value)) {
        result[key] = value
      }
    }
    return result
  }

  getSearchTerms () {
    return { ...INITIAL_SEARCH_TERMS(), ...this.instance }
  }

  getAttributeType (key) {
    return findFieldByKey(key, SEARCH_QUERY_TYPE) || findFieldByKey(key, SEARCH_PARAMS_TYPE)
  }

  getAttributeValueTitle (key, value) {
    value = value || this.getAttributeValue(key)
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      console.error(`AttributeType not found by key ${key}`)
      return
    }

    const filter = this.filters[attributeType.responseKey] || this.filters[attributeType.key]
    const result = []
    if (key === 'boat_types') {
      this.store?.getters['search/boatTypes']
        ?.filter(t => value.map(i => parseInt(i, 10)).includes(parseInt(t.id, 10)))
        ?.forEach((i) => {
          result.push(i?.name)
        })
      if (result.length) {
        return result
      }
    }
    if (key === 'charter_types') {
      this.ctx?.store?.getters['data/chartered']
        ?.filter(t => value === parseInt(t.id, 10))
        ?.forEach((i) => {
          result.push(this.ctx?.i18n?.t ? this.ctx.i18n.t(i?.transTitle) : i?.transTitle)
        })
      if (result.length) {
        return result
      }
    }

    if (filter && Array.isArray(filter) && filter.length) {
      if (Array.isArray(value)) {
        filter.filter(t =>
          value.map(v => `${v}`).includes(`${t.key}`) || value.map(v => `${v}`).includes(`${t.id}`)
        ).forEach((t) => {
          result.push(t.name)
        })
      } else {
        filter.filter(t =>
          `${t.key}` === `${value}` || `${t.id}` === `${value}`
        ).forEach((t) => {
          result.push(t.name)
        })
      }
    }

    if (attributeType.type !== SEARCH_LIST_ATTRIBUTE && result.length) {
      return result[0]
    }

    return result.length ? result : value
  }

  isChangedAttribute (key, value = null) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      console.error(`AttributeType not found by key ${key}`)
      return
    }
    const dataKey = attributeType.dataKey || attributeType.key
    if (value === undefined || value === null) {
      value = this.getAttributeValue(dataKey)
    }
    return this.checkAttributeChangeStatus(dataKey, value) || false
  }

  attributeInList (key, value) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      console.error(`AttributeType not found by key ${key}`)
      return
    }

    const data = this.getAttributeValue(key)
    if (Array.isArray(data)) {
      const compareValue = attributeType.listOf === SEARCH_INTEGER_ATTRIBUTE
        ? parseInt(value, 10)
        : value
      return data.includes(compareValue)
    }

    return false
  }

  toggleAttributeValue (key, value) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      return
    }
    const dataKey = attributeType.dataKey || attributeType.key
    const currentValue = this.getAttributeValue(dataKey) || []

    const compareValue = attributeType.listOf === SEARCH_INTEGER_ATTRIBUTE
      ? parseInt(value, 10)
      : value

    if (currentValue.includes(compareValue)) {
      this.setAttributeValue(
        dataKey,
        currentValue.filter((i) => {
          const itemValue = attributeType.listOf === SEARCH_INTEGER_ATTRIBUTE
            ? parseInt(i, 10)
            : i
          return itemValue !== compareValue
        })
      )
    } else {
      this.setAttributeValue(dataKey, [...currentValue, compareValue])
    }
  }

  // Custom setters

  setLangAttribute (key, value) {
    if (this.getAttributeValue(key) !== value) {
      this.softResetAllAttributes()
      this.defaultAttributeSetter(key, value)
    }
  }

  setStartDateAttribute (key, value) {
    const isValid = isValidDateFormat(value)
    if (isValid) {
      this.defaultAttributeSetter(key, dayjs(new Date(value)).format('YYYY-MM-DD'))
    } else {
      this.defaultAttributeSetter(key, null, true)
      this.setAttributeValue('duration', null, true)
    }
  }

  setCurrencyAttribute (key, currency) {
    currency = this?.ctx?.store?.getters['lang/currentCurrency']
    if (currency) {
      this.defaultAttributeSetter(key, currency)
    }
  }

  setSortByAttribute (key, value) {
    value = this?.ctx?.store?.getters['search/sortOptionsDs'].find(i => i?.route?.query?.sortBy === value)?.route?.query.sortBy
    if (value) {
      this.defaultAttributeSetter(key, value)
    }
  }

  setBoatTypeAttribute (key, value) {
    if (value === SEARCH_BOAT_ANY_TYPE) {
      this.resetAttributeValue('boat_types')
    } else {
      const boatType = findBoatTypeBySlug(this.boatTypes, value)
      if (boatType) {
        if (!this.getAttributeValue('boat_types').includes(parseInt(boatType.id, 10))) {
          this.toggleAttributeValue('boat_types', parseInt(boatType.id, 10))
        }
      }
      this.defaultAttributeSetter(key, value)
    }
  }

  setBoatTypesAttribute (key, value) {
    if (value === SEARCH_BOAT_ANY_TYPE) {
      this.resetAttributeValue(key)
    } else {
      const boatType = findBoatTypeById(this.boatTypes, value)
      const slug = getBoatTypeSlugs(boatType)[0]
      if (boatType) {
        if (this.getAttributeValue('boatType') !== slug) {
          this.defaultAttributeSetter('boatType', slug)
        }
      } else {
        this.defaultAttributeSetter('boatType', SEARCH_BOAT_ANY_TYPE)
      }
      this.defaultAttributeSetter(key, value)
    }
  }

  setCharterTypeAttribute (key, value) {
    const charterType = findCharterTypeBySlug(this.charterTypes, value)
    if (charterType) {
      this.defaultAttributeSetter('charter_types', parseInt(charterType.id, 10))
    } else if (value === this.getDefaultAttributeValue(key)) {
      this.defaultAttributeSetter('charter_types', this.getDefaultAttributeValue('charter_types'), true)
    }
    this.defaultAttributeSetter(key, value, true)
  }

  setCharterTypesAttribute (key, value) {
    const charterType = findCharterTypeById(this.charterTypes, value)
    const slug = charterType?.transTitle
    if (charterType) {
      this.defaultAttributeSetter('charterType', slug)
    } else if (value === this.getDefaultAttributeValue(key)) {
      this.defaultAttributeSetter('charterType', this.getDefaultAttributeValue('charterType'), true)
    }
    this.defaultAttributeSetter(key, value, true)
  }

  setLocationsAttribute (key, value) {
    if (typeof value === 'string') {
      value = value.split(',')
    }
    value = value.filter(v => v && v.substring(0, 3) !== 'in-')
    if (value[0]) {
      this.defaultAttributeSetter('location', value[0])
    }
    this.defaultAttributeSetter(key, value)
  }

  setLocationAttribute (key, value) {
    this.defaultAttributeSetter(key, value)
    this.defaultAttributeSetter('locations', [value, ...(this.getAttributeValue('locations') || [])].filter(v => v && v.substring(0, 3) !== 'in-'))
  }

  // Other Functions
  getFilterUrl (key, value) {
    const attributeType = this.getAttributeType(key)
    if (!attributeType) {
      console.error(`AttributeType not found by key ${key}`)
      return '#'
    }

    const originalInstance = this.instance
    const tempInstance = { ...originalInstance }
    this.instance = tempInstance
    if (attributeType.type === SEARCH_LIST_ATTRIBUTE) {
      if (Array.isArray(value)) {
        value.forEach((i) => {
          this.toggleAttributeValue(key, i)
        })
      } else {
        this.toggleAttributeValue(key, value)
      }
    } else if (attributeType.type === SEARCH_BOOLEAN_ATTRIBUTE) {
      const currentValue = this.getAttributeValue(key)
      this.setAttributeValue(key, currentValue ? null : true)
    } else if (attributeType.type === SEARCH_STRING_ATTRIBUTE || attributeType.type === SEARCH_INTEGER_ATTRIBUTE) {
      const currentValue = this.getAttributeValue(key)
      this.setAttributeValue(key, currentValue == value ? null : value)
    } else if (attributeType.type === SEARCH_MINMAX_ATTRIBUTE) {
      const currentValue = this.getAttributeValue(key)
      if (JSON.stringify(currentValue) === JSON.stringify(value)) {
        this.setAttributeValue(key, { min: null, max: null })
      } else {
        this.setAttributeValue(key, value)
      }
    } else {
      this.setAttributeValue(key, value)
    }
    const urlObject = this.generateClientUrl()
    this.instance = originalInstance

    return this.ctx.app.router.resolve(urlObject).href
  }

  getFiltersUrl (filters, options = { reset: false, apply: false }) {
    if (options?.reset) {
      this.resetAllAttributes()
    }

    const originalInstance = this.instance
    if (!options?.apply) {
      const tempInstance = { ...originalInstance }
      this.instance = tempInstance
    }

    Object.entries(filters).forEach(([key, value]) => {
      const attributeType = this.getAttributeType(key)
      if (!attributeType) {
        console.error(`AttributeType not found by key ${key}`)
        return
      }

      if (attributeType.type === SEARCH_LIST_ATTRIBUTE) {
        this.setAttributeValue(key, value)
      } else if (attributeType.type === SEARCH_BOOLEAN_ATTRIBUTE) {
        this.setAttributeValue(key, value)
      } else if (attributeType.type === SEARCH_STRING_ATTRIBUTE || attributeType.type === SEARCH_INTEGER_ATTRIBUTE) {
        this.setAttributeValue(key, value)
      } else if (attributeType.type === SEARCH_MINMAX_ATTRIBUTE) {
        this.setAttributeValue(key, value)
      } else {
        this.setAttributeValue(key, value)
      }
    })

    const urlObject = this.generateClientUrl()

    this.instance = originalInstance

    return this.ctx.app.router.resolve(urlObject).href
  }
}
